import type {
  ProductCareItem,
  ProductCareItemList,
} from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductInfo.props'

/**
 * Composable for managing product care information.
 *
 * @returns {Object} An object containing product care data and utility methods.
 * @property {ComputedRef<ProductCareItemList>} data - Computed product care items list.
 * @property {Function} getProductCareItemsByID - Method to filter product care items by their IDs.
 *
 * @example
 * const { data, getProductCareItemsByID } = useProductCare()
 * const specificCareItems = getProductCareItemsByID(['item1', 'item2'])
 *
 * @remarks
 * This composable uses useProductFeaturesList to retrieve product care items.
 * It transforms and filters the raw data into a structured ProductCareItemList.
 */
export const useProductCare = () => {
  const { productFeaturesList } = useProductFeaturesList('product-care-items')

  /**
   * Computed product care items with transformed and filtered data.
   *
   * @type {ComputedRef<ProductCareItemList>}
   */
  const productCareItems: ComputedRef<ProductCareItemList> = computed(() => ({
    title: productFeaturesList.value?.title ?? '',
    description: productFeaturesList.value?.description ?? '',
    // TODO: type this better
    items: (
      (productFeaturesList.value?.listOfContentItems as unknown as any[]) ?? []
    )
      .map(i => ({
        id: i.id,
        title: i.title ?? '',
        shortDescription: i.shortDescription,
        longDescription: i.longDescription,
        image: i.image?.secure_url,
      }))
      .filter((i): i is ProductCareItem => !!i.id),
  }))

  /**
   * Retrieves product care items filtered by their IDs.
   *
   * @param {string[]} [items] - Array of product care item IDs to filter.
   * @returns {ProductCareItemList} Filtered product care items list.
   *
   * @example
   * const careItems = getProductCareItemsByID(['item1', 'item2'])
   */
  const getProductCareItemsByID = (items?: string[]): ProductCareItemList => {
    return {
      ...productCareItems.value,
      items: productCareItems.value?.items.filter(
        i => items?.includes(i.id) ?? false
      ),
    }
  }

  return {
    data: productCareItems,
    getProductCareItemsByID,
  }
}
